import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Copy, Plus, Trash2, Pencil, Check, X, Save, Lock, ArrowUpRight, Globe, Loader2, FileUp, File as FileIcon } from 'lucide-react';

import { API, EMBED_DOMAIN } from '@/config';

function UpgradeHint({ text }) {
  return (
    <Link to="/pricing" className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mt-1 hover:underline" data-testid="upgrade-hint">
      <Lock className="h-3 w-3" /> {text} <ArrowUpRight className="h-3 w-3" />
    </Link>
  );
}

export default function WidgetConfig() {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planFeatures, setPlanFeatures] = useState({});
  // General settings
  const [botName, setBotName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [theme, setTheme] = useState('light');
  const [accentColor, setAccentColor] = useState('#10B981');
  const [hideBadge, setHideBadge] = useState(false);
  const [customBadgeText, setCustomBadgeText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [autoOpen, setAutoOpen] = useState(true);
  const [autoOpenDelay, setAutoOpenDelay] = useState(5);
  const [notificationSound, setNotificationSound] = useState(true);
  // Training Files
  const [trainingFiles, setTrainingFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnTitle, setNewAnnTitle] = useState('');
  const [newAnnContent, setNewAnnContent] = useState('');
  const [newAnnImage, setNewAnnImage] = useState('');
  // Crawl
  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [crawledUrl, setCrawledUrl] = useState('');
  const [lastCrawledAt, setLastCrawledAt] = useState('');
  // KB
  const [kb, setKb] = useState([]);
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [editId, setEditId] = useState(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');

  const loadWidget = useCallback(async () => {
    try {
      const [wRes, meRes] = await Promise.all([
        axios.get(`${API}/widgets/${widgetId}`),
        axios.get(`${API}/auth/me`),
      ]);
      const w = wRes.data;
      setWidget(w);
      setBotName(w.bot_name || '');
      setSystemPrompt(w.system_prompt || '');
      setWelcomeMsg(w.welcome_message || '');
      setTheme(w.theme || 'light');
      setAccentColor(w.accent_color || '#10B981');
      setHideBadge(w.hide_badge || false);
      setCustomBadgeText(w.custom_badge_text || '');
      setLogoUrl(w.logo_url || '');
      setAutoOpen(w.auto_open !== false);
      setAutoOpenDelay(w.auto_open_delay || 5);
      setNotificationSound(w.notification_sound !== false);
      setTrainingFiles(w.training_files || []);
      // Load announcements
      try { const annRes = await axios.get(`${API}/widgets/${widgetId}/announcements`); setAnnouncements(annRes.data); } catch (e) { /* Announcements load failed */ }
      setCrawledUrl(w.crawled_url || '');
      setLastCrawledAt(w.last_crawled_at || '');
      setCrawlUrl(w.crawled_url || '');
      setKb(w.knowledge_base || []);
      setPlanFeatures(meRes.data.plan_features || {});
    } catch {
      toast.error('Widget not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [widgetId, navigate]);

  useEffect(() => { loadWidget(); }, [loadWidget]);

  const saveGeneral = async () => {
    setSaving(true);
    try {
      const payload = {
        bot_name: botName,
        system_prompt: systemPrompt,
        welcome_message: welcomeMsg,
        theme,
        accent_color: accentColor,
        hide_badge: hideBadge,
        custom_badge_text: customBadgeText,
        auto_open: autoOpen,
        auto_open_delay: autoOpenDelay,
        notification_sound: notificationSound,
      };
      await axios.put(`${API}/widgets/${widgetId}`, payload);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveKB = async (newKb) => {
    try {
      await axios.put(`${API}/widgets/${widgetId}`, { knowledge_base: newKb });
      setKb(newKb);
      toast.success('Knowledge Base updated');
    } catch { toast.error('Failed to save KB'); }
  };

  const addQA = () => {
    if (!newQ.trim() || !newA.trim()) { toast.error('Both fields required'); return; }
    const updated = [...kb, { id: Date.now().toString(), question: newQ.trim(), answer: newA.trim() }];
    saveKB(updated);
    setNewQ(''); setNewA('');
  };

  const removeQA = (id) => saveKB(kb.filter(q => q.id !== id));
  const startEdit = (qa) => { setEditId(qa.id); setEditQ(qa.question); setEditA(qa.answer); };
  const cancelEdit = () => setEditId(null);
  const saveEdit = () => {
    if (!editQ.trim() || !editA.trim()) { toast.error('Both fields required'); return; }
    saveKB(kb.map(q => q.id === editId ? { ...q, question: editQ.trim(), answer: editA.trim() } : q));
    setEditId(null);
  };

  const embedCode = `<script src="${EMBED_DOMAIN}/api/embed/widget.js?id=${widgetId}"></script>`;
  const copyEmbed = () => { navigator.clipboard.writeText(embedCode); toast.success('Embed code copied!'); };

  const ft = planFeatures;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="widget-config-page">
      <header className="border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-sm font-semibold text-foreground">{widget?.bot_name}</h1>
            <p className="text-xs text-muted-foreground">Widget ID: {widgetId}</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{widget?.messages_used || 0} msgs this month</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Embed Code */}
        <div className="p-4 rounded-xl border border-border bg-card mb-6" data-testid="embed-code-section">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Embed Code</Label>
            <Button size="sm" variant="outline" onClick={copyEmbed} className="gap-1.5" data-testid="copy-embed-code-btn">
              <Copy className="h-3 w-3" /> Copy
            </Button>
          </div>
          <code className="block text-xs bg-muted p-3 rounded-lg text-foreground break-all font-mono" data-testid="embed-code-display">
            {embedCode}
          </code>
          <p className="text-xs text-muted-foreground mt-2">Paste this before the closing &lt;/body&gt; tag of your website.</p>
        </div>

        <Tabs defaultValue="general" data-testid="widget-config-tabs">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="tab-knowledge">Knowledge Base</TabsTrigger>
            <TabsTrigger value="website" data-testid="tab-website">Website Crawl</TabsTrigger>
            <TabsTrigger value="training" data-testid="tab-training">Training Files</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements">News</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <div className="space-y-5" data-testid="general-settings-form">
              {/* Bot Name — always available */}
              <div className="space-y-1.5">
                <Label>Bot Name</Label>
                <Input value={botName} onChange={(e) => setBotName(e.target.value)} data-testid="widget-bot-name-input" />
              </div>

              {/* Welcome Message — available for paid plans */}
              <div className="space-y-1.5">
                <Label>Welcome Message</Label>
                {ft.can_custom_theme ? (
                  <Input value={welcomeMsg} onChange={(e) => setWelcomeMsg(e.target.value)} data-testid="widget-welcome-input" />
                ) : (
                  <>
                    <Input value={welcomeMsg} disabled className="opacity-50" />
                    <UpgradeHint text="Upgrade to Single ($10/mo) to customize" />
                  </>
                )}
              </div>

              {/* System Prompt — available for paid plans */}
              <div className="space-y-1.5">
                <Label>System Prompt</Label>
                {ft.can_custom_theme ? (
                  <>
                    <Textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} rows={5} className="resize-none" data-testid="widget-system-prompt-input" />
                    <p className="text-xs text-muted-foreground">Defines your bot's personality. Knowledge Base Q&A pairs are auto-appended.</p>
                  </>
                ) : (
                  <>
                    <Textarea value={systemPrompt} disabled rows={3} className="resize-none opacity-50" />
                    <UpgradeHint text="Upgrade to Single ($10/mo) to set a custom prompt" />
                  </>
                )}
              </div>

              {/* Theme & Accent — paid plans only */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Theme</Label>
                  {ft.can_custom_theme ? (
                    <div className="flex gap-2">
                      {['light', 'dark'].map(t => (
                        <button key={t} onClick={() => setTheme(t)} className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${theme === t ? 'border-[#10B981] bg-[#10B981]/10 text-[#10B981]' : 'border-border text-muted-foreground hover:border-foreground/20'}`} data-testid={`theme-${t}-btn`}>
                          {t === 'light' ? 'Light' : 'Dark'}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="py-2 text-xs text-muted-foreground border border-border rounded-lg text-center opacity-50">Light (default)</div>
                      <UpgradeHint text="Upgrade to customize theme" />
                    </>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Accent Color</Label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="w-9 h-9 rounded-md border border-border cursor-pointer" data-testid="accent-color-picker" />
                    <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="flex-1 font-mono text-xs" data-testid="accent-color-input" />
                  </div>
                </div>
              </div>

              {/* Badge Controls */}
              <div className="p-4 rounded-xl border border-border bg-card/50 space-y-4">
                <Label className="text-sm font-semibold">Badge Settings</Label>

                {/* Hide badge — Starter ($15) and above */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground">Hide "Powered by Whisper.AI"</p>
                    <p className="text-xs text-muted-foreground">Remove the badge from your widget</p>
                  </div>
                  {ft.can_hide_badge ? (
                    <Switch checked={hideBadge} onCheckedChange={setHideBadge} data-testid="hide-badge-switch" />
                  ) : (
                    <div className="flex flex-col items-end">
                      <Switch disabled checked={false} className="opacity-40" />
                      <UpgradeHint text="Single plan ($10/mo)" />
                    </div>
                  )}
                </div>

                {/* Custom badge text — Pro ($39) and above */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <p className="text-sm text-foreground">Custom badge text</p>
                      <p className="text-xs text-muted-foreground">Replace with your brand: "Powered by Your Company"</p>
                    </div>
                  </div>
                  {ft.can_custom_badge ? (
                    <Input
                      value={customBadgeText}
                      onChange={(e) => setCustomBadgeText(e.target.value)}
                      placeholder="e.g., Acme Inc."
                      disabled={hideBadge}
                      className={hideBadge ? 'opacity-40' : ''}
                      data-testid="custom-badge-input"
                    />
                  ) : (
                    <>
                      <Input value="" disabled placeholder="e.g., Acme Inc." className="opacity-40" />
                      <UpgradeHint text="Starter plan ($15/mo)" />
                    </>
                  )}
                  {ft.can_custom_badge && !hideBadge && customBadgeText && (
                    <p className="text-xs text-muted-foreground mt-1.5">Preview: "Powered by {customBadgeText}"</p>
                  )}
                </div>
              </div>

              {/* Company Logo */}
              <div className="p-4 rounded-xl border border-border bg-card/50 space-y-3">
                <Label className="text-sm font-semibold">Company Logo</Label>
                <p className="text-xs text-muted-foreground">Shown in the chat header next to your bot name</p>

                {ft.can_custom_logo ? (
                  <div className="space-y-3">
                    {logoUrl ? (
                      <div className="flex items-center gap-3">
                        <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-lg object-cover border border-border" data-testid="logo-preview" />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/widgets/${widgetId}/logo`);
                              setLogoUrl('');
                              toast.success('Logo removed');
                            } catch { toast.error('Failed to remove logo'); }
                          }}
                          data-testid="remove-logo-btn"
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No logo uploaded</p>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        id="logo-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 500000) { toast.error('Max file size is 500KB'); return; }
                          setUploadingLogo(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await axios.post(`${API}/widgets/${widgetId}/logo`, formData);
                            setLogoUrl(res.data.logo_url);
                            toast.success('Logo uploaded');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                          finally { setUploadingLogo(false); e.target.value = ''; }
                        }}
                        data-testid="logo-file-input"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => document.getElementById('logo-upload')?.click()}
                        disabled={uploadingLogo}
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1.5">PNG, JPG or SVG. Max 500KB.</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="w-12 h-12 rounded-lg border border-dashed border-border flex items-center justify-center opacity-40">
                      <Lock className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <UpgradeHint text="Pro plan ($39/mo)" />
                  </div>
                )}
              </div>

              {/* Chat Behavior */}
              <div className="p-4 rounded-xl border border-border bg-card/50 space-y-4">
                <Label className="text-sm font-semibold">Chat Behavior</Label>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground">Auto-open chat</p>
                    <p className="text-xs text-muted-foreground">Automatically open chat window when visitor lands on page</p>
                  </div>
                  <Switch checked={autoOpen} onCheckedChange={setAutoOpen} data-testid="auto-open-switch" />
                </div>

                {autoOpen && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Delay (seconds)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min={1}
                        max={30}
                        value={autoOpenDelay}
                        onChange={(e) => setAutoOpenDelay(Math.max(1, Math.min(30, parseInt(e.target.value) || 5)))}
                        className="w-20 text-center"
                        data-testid="auto-open-delay-input"
                      />
                      <span className="text-xs text-muted-foreground">seconds after page load</span>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground">Notification sound</p>
                    <p className="text-xs text-muted-foreground">Play a chime sound when chat auto-opens (once per session)</p>
                  </div>
                  <Switch checked={notificationSound} onCheckedChange={setNotificationSound} data-testid="notification-sound-switch" />
                </div>
              </div>

              <Button onClick={saveGeneral} disabled={saving} className="w-full gap-1.5" data-testid="save-general-btn">
                <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="knowledge">
              <div className="space-y-5" data-testid="kb-settings-form">
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <Label className="text-sm font-medium">Add Q&A Pair</Label>
                  <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Question" data-testid="kb-new-question" />
                  <Textarea value={newA} onChange={(e) => setNewA(e.target.value)} placeholder="Answer" rows={2} className="resize-none" data-testid="kb-new-answer" />
                  <Button size="sm" onClick={addQA} className="gap-1.5" data-testid="kb-add-btn"><Plus className="h-4 w-4" /> Add Pair</Button>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Saved Pairs ({kb.length})</Label>
                  {kb.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8" data-testid="kb-empty">No Q&A pairs yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {kb.map((qa) => (
                        <div key={qa.id} className="p-3 rounded-lg border border-border group" data-testid={`kb-pair-${qa.id}`}>
                          {editId === qa.id ? (
                            <div className="space-y-2">
                              <Input value={editQ} onChange={(e) => setEditQ(e.target.value)} data-testid="kb-edit-question" />
                              <Textarea value={editA} onChange={(e) => setEditA(e.target.value)} rows={2} className="resize-none" data-testid="kb-edit-answer" />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={saveEdit} className="gap-1" data-testid="kb-save-edit"><Check className="h-3 w-3" /> Save</Button>
                                <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid="kb-cancel-edit"><X className="h-3 w-3" /> Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">Q: {qa.question}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">A: {qa.answer}</p>
                              </div>
                              <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(qa)} data-testid={`kb-edit-${qa.id}`}><Pencil className="h-3 w-3" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeQA(qa.id)} data-testid={`kb-delete-${qa.id}`}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

          {/* ─── Website Crawl Tab ─── */}
          <TabsContent value="website">
            <div className="space-y-5" data-testid="crawl-section">
              {ft.can_custom_theme ? (
                <>
                  <div className="p-5 rounded-xl border border-border bg-card">
                    <Label className="text-sm font-semibold mb-1 block">Crawl Your Website</Label>
                    <p className="text-xs text-muted-foreground mb-4">
                      Enter your website URL and we'll automatically extract content to train your bot. The bot will learn from your pages and answer questions about your site.
                    </p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          value={crawlUrl}
                          onChange={(e) => setCrawlUrl(e.target.value)}
                          placeholder="https://yourwebsite.com"
                          className="pl-9"
                          disabled={crawling}
                          data-testid="crawl-url-input"
                        />
                      </div>
                      <Button
                        onClick={async () => {
                          if (!crawlUrl.trim()) { toast.error('Enter a URL'); return; }
                          setCrawling(true);
                          try {
                            const res = await axios.post(`${API}/widgets/${widgetId}/crawl`, { url: crawlUrl.trim(), max_pages: 10 });
                            setCrawledUrl(crawlUrl.trim());
                            setLastCrawledAt(new Date().toISOString());
                            toast.success(`Crawled ${res.data.pages_crawled} pages, added ${res.data.kb_entries_added} KB entries`);
                            loadWidget();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Crawl failed');
                          } finally { setCrawling(false); }
                        }}
                        disabled={crawling || !crawlUrl.trim()}
                        className="gap-1.5 shrink-0"
                        data-testid="crawl-btn"
                      >
                        {crawling ? <><Loader2 className="h-4 w-4 animate-spin" /> Crawling...</> : 'Crawl Site'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {ft.can_custom_logo
                        ? 'Pro/Business: Up to 20 pages per crawl'
                        : 'Up to 5 pages per crawl. Upgrade to Pro for up to 20 pages.'
                      }
                    </p>
                  </div>

                  {/* Crawl status */}
                  {crawledUrl && (
                    <div className="p-4 rounded-xl border border-border bg-card/50" data-testid="crawl-status">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <p className="text-sm font-medium text-foreground">Website crawled</p>
                          </div>
                          <p className="text-xs text-muted-foreground">{crawledUrl}</p>
                          {lastCrawledAt && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Last crawled: {new Date(lastCrawledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              setCrawling(true);
                              try {
                                const res = await axios.post(`${API}/widgets/${widgetId}/crawl`, { url: crawledUrl, max_pages: 10 });
                                setLastCrawledAt(new Date().toISOString());
                                toast.success(`Re-crawled ${res.data.pages_crawled} pages`);
                                loadWidget();
                              } catch (err) { toast.error('Re-crawl failed'); }
                              finally { setCrawling(false); }
                            }}
                            disabled={crawling}
                            data-testid="recrawl-btn"
                          >
                            {crawling ? 'Crawling...' : 'Re-crawl'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={async () => {
                              if (!window.confirm('Remove all crawled data from knowledge base?')) return;
                              try {
                                await axios.delete(`${API}/widgets/${widgetId}/crawl`);
                                setCrawledUrl('');
                                setLastCrawledAt('');
                                toast.success('Crawled data removed');
                                loadWidget();
                              } catch { toast.error('Failed to remove'); }
                            }}
                            data-testid="remove-crawl-btn"
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Crawled KB entries count */}
                  {kb.some(q => q.crawled) && (
                    <div className="p-3 rounded-lg bg-muted/50 border border-border">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{kb.filter(q => q.crawled).length}</span> pages crawled into Knowledge Base
                        {' · '}
                        <span className="font-medium text-foreground">{kb.filter(q => !q.crawled).length}</span> manual Q&A pairs
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 border border-dashed border-border rounded-xl">
                  <Globe className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-muted-foreground">Website crawl requires Single plan ($10/mo) or higher</p>
                  <Link to="/pricing" className="text-sm text-[#10B981] hover:underline inline-flex items-center gap-1 mt-2 font-medium">
                    Upgrade <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── Training Files Tab ─── */}
          <TabsContent value="training">
            <div className="space-y-5" data-testid="training-files-section">
              {ft.training_files_limit > 0 ? (
                <>
                  <div className="p-5 rounded-xl border border-border bg-card">
                    <Label className="text-sm font-semibold mb-1 block">Upload Training Files</Label>
                    <p className="text-xs text-muted-foreground mb-4">
                      Upload documents to train your bot. Supported: PDF, TXT, CSV, DOCX. The bot will learn from the content and use it to answer questions.
                    </p>
                    <div>
                      <input
                        type="file"
                        accept=".pdf,.txt,.csv,.docx"
                        id="training-file-upload"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const maxMb = ft.training_file_max_mb || 2;
                          if (file.size > maxMb * 1024 * 1024) { toast.error(`Max file size is ${maxMb}MB`); return; }
                          setUploadingFile(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await axios.post(`${API}/widgets/${widgetId}/training-files`, formData);
                            toast.success(res.data.message);
                            loadWidget();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                          finally { setUploadingFile(false); e.target.value = ''; }
                        }}
                        data-testid="training-file-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => document.getElementById('training-file-upload')?.click()}
                        disabled={uploadingFile || trainingFiles.length >= ft.training_files_limit}
                        className="gap-2"
                        data-testid="upload-training-file-btn"
                      >
                        {uploadingFile ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</> : <><FileUp className="h-4 w-4" /> Upload File</>}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        {trainingFiles.length} / {ft.training_files_limit === 999 ? '∞' : ft.training_files_limit} files · Max {ft.training_file_max_mb}MB per file · PDF, TXT, CSV, DOCX
                      </p>
                    </div>
                  </div>

                  {/* File list */}
                  {trainingFiles.length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-border rounded-xl">
                      <FileUp className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-40" />
                      <p className="text-sm text-muted-foreground">No training files yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Upload a PDF, TXT, CSV, or DOCX to train your bot</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{trainingFiles.length} File{trainingFiles.length !== 1 ? 's' : ''} Uploaded</Label>
                      {trainingFiles.map((f) => (
                        <div key={f.file_id} className="p-3 rounded-lg border border-border bg-card flex items-center justify-between group" data-testid={`training-file-${f.file_id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-[#10B981]/10 flex items-center justify-center shrink-0">
                              <FileIcon className="h-4 w-4 text-[#10B981]" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{f.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {(f.size_bytes / 1024).toFixed(0)}KB · .{f.ext} · {new Date(f.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={async () => {
                              if (!window.confirm(`Delete ${f.filename}? This removes its training data from the bot.`)) return;
                              try {
                                await axios.delete(`${API}/widgets/${widgetId}/training-files/${f.file_id}`);
                                toast.success('File deleted');
                                loadWidget();
                              } catch { toast.error('Failed to delete'); }
                            }}
                            data-testid={`delete-file-${f.file_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 border border-dashed border-border rounded-xl">
                  <FileUp className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-muted-foreground">Training Files requires Single plan ($10/mo) or higher</p>
                  <p className="text-xs text-muted-foreground mt-1">Upload PDFs, documents, and spreadsheets to train your bot</p>
                  <Link to="/pricing" className="text-sm text-[#10B981] hover:underline inline-flex items-center gap-1 mt-3 font-medium">
                    Upgrade <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── Announcements Tab ─── */}
          <TabsContent value="announcements">
            <div className="space-y-5" data-testid="announcements-section">
              {ft.can_custom_theme ? (
                <>
                  <div className="p-5 rounded-xl border border-border bg-card space-y-3">
                    <Label className="text-sm font-semibold">Add Announcement</Label>
                    <p className="text-xs text-muted-foreground">Push news and updates to your widget visitors via the News tab.</p>
                    <Input value={newAnnTitle} onChange={(e) => setNewAnnTitle(e.target.value)} placeholder="Title" data-testid="ann-title-input" />
                    <textarea value={newAnnContent} onChange={(e) => setNewAnnContent(e.target.value)} placeholder="Content" rows={3} className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm resize-none outline-none focus:ring-1 focus:ring-ring" data-testid="ann-content-input" />
                    <Input value={newAnnImage} onChange={(e) => setNewAnnImage(e.target.value)} placeholder="Cover image URL (optional)" data-testid="ann-image-input" />
                    <Button size="sm" onClick={async () => {
                      if (!newAnnTitle.trim() || !newAnnContent.trim()) { toast.error('Title and content required'); return; }
                      try {
                        await axios.post(`${API}/widgets/${widgetId}/announcements`, { title: newAnnTitle, content: newAnnContent, cover_image: newAnnImage });
                        setNewAnnTitle(''); setNewAnnContent(''); setNewAnnImage('');
                        toast.success('Announcement published');
                        loadWidget();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }} data-testid="ann-publish-btn">Publish</Button>
                  </div>
                  {announcements.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">No announcements yet. Publish your first one above.</p>
                  ) : (
                    <div className="space-y-2">
                      {announcements.map(a => (
                        <div key={a.ann_id} className="p-3 rounded-lg border border-border flex items-center justify-between group" data-testid={`ann-${a.ann_id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{a.title}</p>
                            <p className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={async () => {
                            try { await axios.delete(`${API}/widgets/${widgetId}/announcements/${a.ann_id}`); toast.success('Deleted'); loadWidget(); } catch { toast.error('Failed'); }
                          }} data-testid={`delete-ann-${a.ann_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 border border-dashed border-border rounded-xl">
                  <p className="text-sm text-muted-foreground">Announcements require a paid plan</p>
                  <Link to="/pricing" className="text-sm text-[#10B981] hover:underline mt-2 inline-block">Upgrade</Link>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
